/**
 * The page a customer's link opens.
 *
 * No session, no row level security to satisfy, no data of ours in the bundle:
 * everything comes from the `presented-document` Edge Function, which is the
 * only thing holding a key. If that function is unreachable this page shows a
 * phone number, not a stack trace.
 *
 * The same page is what "Sign now on this device" opens at the kitchen table —
 * one signing surface, so there is no second code path to keep honest.
 */

import {
  signedDateWords,
  type DocumentSignature,
  type PaymentMethod,
  type PresentedDocument,
} from "@roof-hawk/core";
import { useCallback, useEffect, useState } from "react";

import { PaymentChoicePage, type PayBlock } from "./PaymentChoicePage";
import {
  PresentedDocumentView,
  type SignaturePayload,
} from "./PresentedDocumentView";

/** `/p/<32 hex characters>` and nothing else. */
export function presentTokenFromPath(pathname: string): string | null {
  const match = /^\/p\/([0-9a-f]{32})\/?$/.exec(pathname);
  return match ? match[1]! : null;
}

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/presented-document`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

type Loaded =
  | { state: "loading" }
  | {
      state: "open";
      document: PresentedDocument;
      companySignedAt: string | null;
    }
  | { state: "closed"; message: string }
  /**
   * Signed, and now being asked how they would like to pay.
   *
   * This carried a sentence and nothing else until 2026-09-06 — "Signed. Thank
   * you, we will be in touch straight away" — which was the end of the road for
   * a customer who had just agreed to spend eighteen thousand dollars.
   */
  | { state: "signed"; pay: PayBlock; copy: SignedCopy | null }
  | { state: "error" };

/**
 * Their copy of what they signed, on the page the letter links to.
 *
 * The frozen snapshot and their own answer — the same two things the office
 * reads in Docs (ADR 0032), rendered by the same component, so a customer and
 * a rep looking at one contract are looking at one document.
 */
export interface SignedCopy {
  document: PresentedDocument;
  companySignedAt: string | null;
  signature: DocumentSignature | null;
  initials: Record<string, unknown>;
}

/** The copy off a "signed" response, or null on one that carries none. */
function signedCopyFrom(body: Record<string, unknown>): SignedCopy | null {
  const record = body.record as {
    signature?: DocumentSignature | null;
    initials?: Record<string, unknown>;
  } | null;
  if (!body.document || !record?.signature) return null;
  return {
    document: body.document as PresentedDocument,
    companySignedAt: (body.companySignedAt as string | null) ?? null,
    signature: record.signature,
    initials: record.initials ?? {},
  };
}

export function PresentPage({ token }: { token: string }) {
  const [loaded, setLoaded] = useState<Loaded>({ state: "loading" });
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Every call is a POST, reading included.
   *
   * The token is the only credential in this flow, and a query string is
   * written to the platform's request log as a matter of course, kept in
   * browser history, and sent along in a Referer. So it travels in a body.
   */
  const call = useCallback(
    async (action: "open" | "sign" | "decline" | "pay", payload?: object) => {
      const response = await fetch(FUNCTION_URL, {
        method: "POST",
        headers: {
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token, action, ...payload }),
      });
      return { response, body: await response.json() };
    },
    [token],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { response, body } = await call("open");
        if (cancelled) return;
        if (response.ok && body.state === "open") {
          setLoaded({
            state: "open",
            document: body.document as PresentedDocument,
            companySignedAt: body.companySignedAt ?? null,
          });
        } else if (response.ok && body.state === "signed" && body.pay) {
          // They signed and came back — from our email, a bookmark, or a
          // refresh. The same three choices, whatever they already picked, and
          // their copy of the contract underneath.
          setLoaded({
            state: "signed",
            pay: body.pay as PayBlock,
            copy: signedCopyFrom(body),
          });
        } else if (body?.message) {
          setLoaded({ state: "closed", message: body.message });
        } else {
          setLoaded({ state: "error" });
        }
      } catch {
        if (!cancelled) setLoaded({ state: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [call]);

  const sign = async (signature: SignaturePayload) => {
    setSigning(true);
    setError(null);
    try {
      const { response, body } = await call("sign", signature);
      if (response.ok && body.state === "signed") {
        setLoaded({
          state: "signed",
          pay: body.pay as PayBlock,
          copy: signedCopyFrom(body),
        });
      } else {
        setError(
          body?.error ??
            body?.message ??
            "That did not go through. Please try again, or call us.",
        );
      }
    } catch {
      setError("That did not go through. Please try again, or call us.");
    } finally {
      setSigning(false);
    }
  };

  /*
   * There is no decline button any more — Jonathon, 2026-08-13. The Edge
   * Function still accepts a "decline" action, because a proposal the office
   * withdraws goes through the same path, and because a link somebody already
   * has must not start erroring.
   */

  if (loaded.state === "loading") return <div className="pdoc-wait" />;

  if (loaded.state === "error") {
    return (
      <Message
        title="We cannot open this right now"
        body="Please call us and we will sort it out."
      />
    );
  }

  if (loaded.state === "signed") {
    const copy = loaded.copy;
    return (
      <>
        <PaymentChoicePage
          pay={loaded.pay}
          onChoose={async (method: PaymentMethod) => {
            try {
              const { response, body } = await call("pay", { method });
              if (!response.ok) {
                return { error: body?.error as string | undefined };
              }
              // The block comes back with the choice recorded on it, so a
              // failed redirect still leaves the page telling the truth.
              if (body?.pay) {
                setLoaded({ state: "signed", pay: body.pay, copy });
              }
              return { paymentUrl: body?.paymentUrl as string | null };
            } catch {
              return {
                error:
                  "We could not reach our payment page. Please try again, or " +
                  "call us.",
              };
            }
          }}
        />
        {copy ? <SignedCopyPanel copy={copy} /> : null}
      </>
    );
  }

  if (loaded.state === "closed") {
    return <Message title="This estimate" body={loaded.message} />;
  }

  return (
    <PresentedDocumentView
      document={loaded.document}
      companySignedAt={loaded.companySignedAt}
      onSign={sign}
      signing={signing}
      error={error}
    />
  );
}

/**
 * "Your signed agreement", under the payment cards.
 *
 * The email we send at signing points here, so this is the copy the customer
 * keeps: closed by default, because somebody who has just signed is being
 * asked to pay and does not need the contract unrolled underneath that, and
 * open in one tap for the one who came back to read it.
 *
 * Printing is the browser's, which is also how a customer saves it as a PDF.
 * Nothing is generated here — the record on this page is the frozen document
 * itself, and a second rendering of it would be a second version to keep
 * honest.
 */
function SignedCopyPanel({ copy }: { copy: SignedCopy }) {
  const [open, setOpen] = useState(false);
  // In words, the way the letter that links here says it — never the raw
  // yyyy-mm-dd, and never through `new Date`, which reads it as the day before.
  const signedOn =
    signedDateWords(copy.signature?.signedDate) ?? copy.signature?.signedDate;
  return (
    <div className="pdoc-copy">
      <div className="pdoc-copy-head">
        <div>
          <h2>Your signed agreement</h2>
          <p>
            {signedOn
              ? `Signed on ${signedOn}. This is your copy — read it, print it, or save it.`
              : "This is your copy — read it, print it, or save it."}
          </p>
        </div>
        <div className="pdoc-copy-acts">
          <button
            type="button"
            className="pdoc-copy-btn"
            aria-expanded={open}
            onClick={() => setOpen((was) => !was)}
          >
            {open ? "Hide the agreement" : "Read the agreement"}
          </button>
          {open ? (
            <button
              type="button"
              className="pdoc-copy-btn"
              onClick={() => window.print()}
            >
              Print or save
            </button>
          ) : null}
        </div>
      </div>

      {open ? (
        <PresentedDocumentView
          document={copy.document}
          companySignedAt={copy.companySignedAt}
          onSign={() => undefined}
          record={{ signature: copy.signature, initials: copy.initials }}
        />
      ) : null}
    </div>
  );
}

function Message({ title, body }: { title: string; body: string }) {
  return (
    <div className="pdoc pdoc-message">
      <h1>{title}</h1>
      <p>{body}</p>
    </div>
  );
}
